/**
 * UserListItem Component
 * Reusable component for displaying a user in lists (search results, followers, following)
 */

import { useFollow } from '@hooks/useFollow';
import type { SearchResult } from '@hooks/useUserSearch';
import { BadgeCheck } from 'lucide-react';
import type { ReactElement } from 'react';

interface UserListItemProps {
  user: SearchResult;
  onViewProfile: () => void;
}

export function UserListItem({ user, onViewProfile }: UserListItemProps): ReactElement {
  const { isFollowing, loading, followUser, unfollowUser } = useFollow(user.id);

  const handleFollowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFollowing) {
      unfollowUser();
    } else {
      followUser();
    }
  };

  return (
    <div
      data-testid="user-list-item"
      className="flex items-center justify-between p-4 rounded-lg hover:bg-theme-surface-hover/50 transition-colors cursor-pointer"
      onClick={onViewProfile}
    >
      {/* Left: User info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shrink-0">
          <span className="text-white font-semibold text-lg">
            {user.displayName?.[0]?.toUpperCase() || '?'}
          </span>
        </div>

        {/* User details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-100 truncate">{user.displayName}</span>
            {user.isVerified && <BadgeCheck className="w-4 h-4 text-theme-primary shrink-0" />}
          </div>
          <div className="flex items-center gap-2 text-sm text-theme-text-secondary">
            <span>{user.followerCount} followers</span>
          </div>
        </div>
      </div>

      {/* Right: Follow button */}
      <button
        type="button"
        data-testid="follow-button"
        data-following={isFollowing ? 'true' : 'false'}
        data-loading={loading ? 'true' : 'false'}
        onClick={handleFollowClick}
        disabled={loading}
        className={`
          px-4 py-2 rounded-lg font-medium transition-colors shrink-0
          ${
            isFollowing
              ? 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-border'
              : 'bg-theme-primary-fill text-theme-on-primary-fill hover:bg-theme-primary-fill-hover'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        {loading ? 'Loading...' : isFollowing ? 'Following' : 'Follow'}
      </button>
    </div>
  );
}
